/**
 * 統一日誌管理系統
 * 支援多級別日誌控制和環境變數配置
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

export interface LogContext {
  [key: string]: any
}

class Logger {
  private static instance: Logger
  private currentLevel: LogLevel = LogLevel.INFO
  private allowUrlControl: boolean = false
  private useCollapsedGroups: boolean = true // 預設啟用折疊功能
  private initialized: boolean = false

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger()
    }
    return Logger.instance
  }

  /**
   * 初始化日誌系統
   * 必須在應用程式啟動時調用
   */
  init(): void {
    if (this.initialized) return

    // 從環境變數讀取日誌級別
    const levelStr = import.meta.env.VITE_LOG_LEVEL || 'INFO'
    this.currentLevel = this.parseLogLevel(levelStr)

    // 是否允許 URL 參數控制日誌級別
    this.allowUrlControl = import.meta.env.VITE_ALLOW_URL_LOG_CONTROL === 'true'

    // 是否啟用折疊群組 (預設啟用，可透過環境變數控制)
    this.useCollapsedGroups = import.meta.env.VITE_ENABLE_COLLAPSED_LOGS !== 'false'

    // 從 URL 參數覆蓋日誌級別（僅在允許時）
    if (this.allowUrlControl) {
      this.initFromUrl()
    }

    this.initialized = true
    this.info('Logger initialized', { 
      level: LogLevel[this.currentLevel],
      allowUrlControl: this.allowUrlControl,
      useCollapsedGroups: this.useCollapsedGroups
    })
  }

  /**
   * 從 URL 參數初始化日誌級別
   */
  private initFromUrl(): void {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const logLevel = urlParams.get('logLevel')
      if (logLevel) {
        const level = this.parseLogLevel(logLevel)
        if (level !== LogLevel.NONE) {
          this.setLevel(level)
          console.info(`🔧 Logger level overridden by URL parameter: ${LogLevel[level]}`)
        }
      }
    } catch (error) {
      console.warn('Failed to parse URL log level:', error)
    }
  }

  /**
   * 解析日誌級別字符串
   */
  private parseLogLevel(levelStr: string): LogLevel {
    const upperLevel = levelStr.toUpperCase()
    if (upperLevel in LogLevel) {
      return LogLevel[upperLevel as keyof typeof LogLevel]
    }
    return LogLevel.INFO // 預設值
  }

  /**
   * 動態設定日誌級別
   */
  setLevel(level: LogLevel): void {
    const oldLevel = this.currentLevel
    this.currentLevel = level
    if (this.initialized && level !== oldLevel) {
      console.info(`🔧 Logger level changed from ${LogLevel[oldLevel]} to ${LogLevel[level]}`)
    }
  }

  /**
   * 獲取當前日誌級別
   */
  getLevel(): LogLevel {
    return this.currentLevel
  }

  /**
   * 設定是否使用折疊群組
   */
  setUseCollapsedGroups(enabled: boolean): void {
    const oldValue = this.useCollapsedGroups
    this.useCollapsedGroups = enabled
    if (this.initialized && enabled !== oldValue) {
      console.info(`🔧 Logger collapsed groups ${enabled ? 'enabled' : 'disabled'}`)
    }
  }

  /**
   * 獲取當前折疊群組設定
   */
  getUseCollapsedGroups(): boolean {
    return this.useCollapsedGroups
  }

  /**
   * 檢查是否已初始化
   */
  isInitialized(): boolean {
    return this.initialized
  }

  /**
   * 檢查是否應該輸出指定級別的日誌
   */
  private shouldLog(level: LogLevel): boolean {
    return this.currentLevel <= level
  }

  /**
   * 格式化日誌訊息
   */
  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString()
    const levelStr = LogLevel[level].padEnd(5)
    
    let formattedMessage = `[${levelStr}] ${timestamp} - ${message}`
    
    if (context && Object.keys(context).length > 0) {
      formattedMessage += ` | Context: ${JSON.stringify(context)}`
    }
    
    return formattedMessage
  }

  /**
   * 檢查是否應該使用折疊群組顯示
   */
  private shouldUseCollapsedGroup(message: string, context?: LogContext): boolean {
    // 如果折疊功能被關閉，直接返回 false
    if (!this.useCollapsedGroups) return false
    
    // 如果訊息長度超過 100 字元，或 context 序列化後超過 200 字元
    const contextStr = context ? JSON.stringify(context) : ''
    return message.length > 100 || contextStr.length > 200
  }

  /**
   * 建立摘要訊息
   */
  private createSummary(message: string, context?: LogContext): string {
    // 截取訊息前 80 字元
    let summary = message.length > 80 ? message.substring(0, 80) + '...' : message
    
    // 添加 context 的關鍵資訊
    if (context) {
      const keyInfo = []
      if (context.method) keyInfo.push(`${context.method}`)
      if (context.status) keyInfo.push(`${context.status}`)
      if (context.url) {
        const url = context.url.toString()
        const shortUrl = url.length > 50 ? '...' + url.slice(-50) : url
        keyInfo.push(shortUrl)
      }
      if (context.userId) keyInfo.push(`user:${context.userId}`)
      if (context.code) keyInfo.push(`code:${context.code}`)
      
      if (keyInfo.length > 0) {
        summary += ` [${keyInfo.join(' | ')}]`
      }
    }
    
    return summary
  }

  /**
   * 獲取日誌級別對應的表情符號
   */
  private getLevelIcon(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG: return '🔍'
      case LogLevel.INFO: return 'ℹ️'
      case LogLevel.WARN: return '⚠️'
      case LogLevel.ERROR: return '❌'
      default: return '📝'
    }
  }

  /**
   * 標準化 context 參數 - 處理 unknown 類型
   */
  private normalizeContext(context: LogContext | unknown): LogContext {
    if (context === null || context === undefined) {
      return {}
    }
    
    if (context instanceof Error) {
      return {
        message: context.message,
        stack: context.stack,
        name: context.name,
        ...(context.cause && { cause: context.cause })
      }
    }
    
    if (typeof context === 'object') {
      return context as LogContext
    }
    
    return { value: String(context) }
  }

  /**
   * 通用日誌輸出方法
   */
  private logWithCollapse(level: LogLevel, message: string, context?: LogContext | unknown): void {
    const normalizedContext = this.normalizeContext(context)
    if (!this.shouldLog(level)) return
    
    const icon = this.getLevelIcon(level)
    const timestamp = new Date().toISOString()
    const levelStr = LogLevel[level].padEnd(5)
    
    // 決定是否使用折疊群組
    if (this.shouldUseCollapsedGroup(message, normalizedContext)) {
      // 使用折疊群組
      const summary = this.createSummary(message, normalizedContext)
      const groupLabel = `${icon} [${levelStr}] ${timestamp} - ${summary}`
      
      console.groupCollapsed(groupLabel)
      console.log(`📝 完整訊息: ${message}`)
      
      if (normalizedContext && Object.keys(normalizedContext).length > 0) {
        console.log(`📋 Context 詳情:`)
        console.table(normalizedContext)
      }
      
      console.groupEnd()
    } else {
      // 使用一般日誌
      const formatted = this.formatMessage(level, message, normalizedContext)
      const logMethod = this.getLogMethod(level)
      logMethod(`${icon} ${formatted}`)
    }
  }

  /**
   * 取得對應的 console 方法
   */
  private getLogMethod(level: LogLevel): (...args: any[]) => void {
    switch (level) {
      case LogLevel.DEBUG:
        return console.log
      case LogLevel.INFO:
        return console.info
      case LogLevel.WARN:
        return console.warn
      case LogLevel.ERROR:
        return console.error
      default:
        return console.log
    }
  }

  /**
   * DEBUG 級別日誌
   */
  debug(message: string, context?: LogContext | unknown): void {
    this.logWithCollapse(LogLevel.DEBUG, message, context)
  }

  /**
   * INFO 級別日誌
   */
  info(message: string, context?: LogContext | unknown): void {
    this.logWithCollapse(LogLevel.INFO, message, context)
  }

  /**
   * WARN 級別日誌
   */
  warn(message: string, context?: LogContext | unknown): void {
    this.logWithCollapse(LogLevel.WARN, message, context)
  }

  /**
   * API 相關日誌 (DEBUG 級別)
   */
  api(message: string, context?: LogContext | unknown): void {
    // 標準化 context
    const normalizedContext = this.normalizeContext(context)
    
    // API 日誌使用特殊的處理
    if (!this.shouldLog(LogLevel.DEBUG)) return
    
    const timestamp = new Date().toISOString()
    const apiMessage = `[API] ${message}`
    
    // 決定是否使用折疊群組
    if (this.shouldUseCollapsedGroup(apiMessage, normalizedContext)) {
      // 使用折疊群組，但添加特殊的 API 標記
      const summary = this.createApiSummary(message, normalizedContext)
      const groupLabel = `🌐 [DEBUG] ${timestamp} - ${summary}`
      
      console.groupCollapsed(groupLabel)
      console.log(`📡 API 請求: ${message}`)
      
      if (normalizedContext && Object.keys(normalizedContext).length > 0) {
        console.log(`📊 API 詳情:`)
        
        // 特殊處理 API 回應資料
        if (normalizedContext.data && typeof normalizedContext.data === 'object') {
          console.log(`📋 基本資訊:`)
          const basicInfo = { ...normalizedContext }
          delete basicInfo.data
          console.table(basicInfo)
          
          console.log(`📦 回應資料:`)
          console.log(normalizedContext.data)
        } else {
          console.table(normalizedContext)
        }
      }
      
      console.groupEnd()
    } else {
      // 使用一般日誌
      this.debug(apiMessage, normalizedContext)
    }
  }

  /**
   * 建立 API 日誌的摘要
   */
  private createApiSummary(message: string, context?: LogContext): string {
    // 截取訊息前 60 字元
    let summary = message.length > 60 ? message.substring(0, 60) + '...' : message
    
    // 添加 API 相關的關鍵資訊
    if (context) {
      const keyInfo = []
      if (context.method) keyInfo.push(context.method)
      if (context.status) {
        const status = context.status.toString()
        const statusIcon = status.startsWith('2') ? '✅' : status.startsWith('4') ? '⚠️' : status.startsWith('5') ? '❌' : ''
        keyInfo.push(`${statusIcon}${status}`)
      }
      if (context.url) {
        const url = context.url.toString()
        const shortUrl = url.includes('/api/') ? 
          '...' + url.substring(url.indexOf('/api/')) : 
          (url.length > 40 ? '...' + url.slice(-40) : url)
        keyInfo.push(shortUrl)
      }
      if (context.duration) keyInfo.push(`${context.duration}ms`)
      
      if (keyInfo.length > 0) {
        summary += ` [${keyInfo.join(' | ')}]`
      }
    }
    
    return summary
  }

  /**
   * ERROR 級別日誌
   */
  error(message: string, context?: LogContext | unknown): void {
    this.logWithCollapse(LogLevel.ERROR, message, context)
  }

  /**
   * 組件相關日誌 (DEBUG 級別)
   */
  component(message: string, context?: LogContext | unknown): void {
    this.debug(`[COMPONENT] ${message}`, context)
  }

  /**
   * 路由相關日誌 (DEBUG 級別)
   */
  router(message: string, context?: LogContext | unknown): void {
    this.debug(`[ROUTER] ${message}`, context)
  }

  /**
   * 服務相關日誌 (DEBUG 級別)
   */
  service(message: string, context?: LogContext | unknown): void {
    this.debug(`[SERVICE] ${message}`, context)
  }

  /**
   * 認證相關日誌 (INFO 級別，但可能包含敏感資訊)
   */
  auth(message: string, context?: LogContext | unknown): void {
    // 標準化並過濾敏感資訊
    const normalizedContext = this.normalizeContext(context)
    const safeContext = Object.keys(normalizedContext).length > 0 ? this.sanitizeAuthContext(normalizedContext) : undefined
    
    // 認證日誌使用特殊的圖示和格式
    if (!this.shouldLog(LogLevel.INFO)) return
    
    const timestamp = new Date().toISOString()
    const authMessage = `[AUTH] ${message}`
    
    // 決定是否使用折疊群組
    if (this.shouldUseCollapsedGroup(authMessage, safeContext)) {
      // 使用折疊群組，但添加特殊的認證標記
      const summary = this.createAuthSummary(message, safeContext)
      const groupLabel = `🔐 [AUTH ] ${timestamp} - ${summary}`
      
      console.groupCollapsed(groupLabel)
      console.log(`🔑 認證流程: ${message}`)
      
      if (safeContext && Object.keys(safeContext).length > 0) {
        console.log(`📋 認證詳情:`)
        console.table(safeContext)
      }
      
      console.groupEnd()
    } else {
      // 使用一般日誌
      const formatted = `[AUTH ] ${timestamp} - ${authMessage}`
      if (safeContext && Object.keys(safeContext).length > 0) {
        console.info(`🔐 ${formatted} | Context: ${JSON.stringify(safeContext)}`)
      } else {
        console.info(`🔐 ${formatted}`)
      }
    }
  }

  /**
   * 建立認證日誌的摘要
   */
  private createAuthSummary(message: string, context?: LogContext): string {
    // 截取訊息前 60 字元
    let summary = message.length > 60 ? message.substring(0, 60) + '...' : message
    
    // 添加認證相關的關鍵資訊
    if (context) {
      const keyInfo = []
      if (context.isAdmin !== undefined) keyInfo.push(context.isAdmin ? 'ADMIN' : 'USER')
      if (context.channelId) keyInfo.push(`ch:${context.channelId}`)
      if (context.userId) keyInfo.push(`user:${context.userId}`)
      if (context.environment) keyInfo.push(context.environment)
      if (context.success !== undefined) keyInfo.push(context.success ? '✅' : '❌')
      if (context.currentRoleType) keyInfo.push(`role:${context.currentRoleType}`)
      
      if (keyInfo.length > 0) {
        summary += ` [${keyInfo.join(' | ')}]`
      }
    }
    
    return summary
  }

  /**
   * 清理認證相關的敏感資訊
   */
  private sanitizeAuthContext(context: LogContext): LogContext {
    const sanitized = { ...context }
    
    // 遮蔽敏感欄位
    const sensitiveFields = ['password', 'token', 'secret', 'key', 'authorization']
    
    Object.keys(sanitized).forEach(key => {
      const lowerKey = key.toLowerCase()
      if (sensitiveFields.some(field => lowerKey.includes(field))) {
        if (typeof sanitized[key] === 'string' && sanitized[key].length > 8) {
          sanitized[key] = sanitized[key].substring(0, 8) + '...'
        } else {
          sanitized[key] = '***'
        }
      }
    })
    
    return sanitized
  }

  /**
   * 性能測量
   */
  time(label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.time(`⏱️ ${label}`)
    }
  }

  /**
   * 結束性能測量
   */
  timeEnd(label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.timeEnd(`⏱️ ${label}`)
    }
  }

  /**
   * 群組日誌開始
   */
  group(label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.group(`📁 ${label}`)
    }
  }

  /**
   * 群組日誌結束
   */
  groupEnd(): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.groupEnd()
    }
  }

  /**
   * 表格形式輸出數據 (DEBUG 級別)
   */
  table(data: any, label?: string): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return
    
    if (label) {
      console.log(`📊 ${label}:`)
    }
    console.table(data)
  }
}

// 匯出單例實例
export const logger = Logger.getInstance()

// 匯出便捷方法 - 確保 logger 已初始化
export const debug = (message: string, context?: LogContext | unknown) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.debug(message, context)
}

export const info = (message: string, context?: LogContext | unknown) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.info(message, context)
}

export const warn = (message: string, context?: LogContext | unknown) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.warn(message, context)
}

export const error = (message: string, context?: LogContext | unknown) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.error(message, context)
}

export const api = (message: string, context?: LogContext | unknown) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.api(message, context)
}

export const component = (message: string, context?: LogContext | unknown) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.component(message, context)
}

export const router = (message: string, context?: LogContext | unknown) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.router(message, context)
}

export const service = (message: string, context?: LogContext | unknown) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.service(message, context)
}

export const auth = (message: string, context?: LogContext | unknown) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.auth(message, context)
}

export const time = (label: string) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.time(label)
}

export const timeEnd = (label: string) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.timeEnd(label)
}

export const group = (label: string) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.group(label)
}

export const groupEnd = () => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.groupEnd()
}

export const table = (data: any, label?: string) => {
  const instance = Logger.getInstance()
  if (!instance.isInitialized()) instance.init()
  return instance.table(data, label)
}

// 延遲設定全域訪問，避免模組初始化問題
setTimeout(() => {
  if (typeof window !== 'undefined') {
    try {
      const isDev = import.meta.env.DEV === true || import.meta.env.NODE_ENV === 'development'
      if (isDev) {
        const loggerInstance = Logger.getInstance()
        if (!loggerInstance.isInitialized()) {
          loggerInstance.init()
        }
        (window as any).logger = loggerInstance
        (window as any).toggleLogCollapse = () => {
          const current = loggerInstance.getUseCollapsedGroups()
          loggerInstance.setUseCollapsedGroups(!current)
          console.info(`🔧 Log collapse is now ${!current ? 'enabled' : 'disabled'}. Try: logger.api('Test message', {data: {large: 'object'.repeat(50)}})`)
        }
        console.log('🔧 Development logger tools available: logger, toggleLogCollapse()')
      }
    } catch (error) {
      // 忽略環境變數檢查錯誤，但不在生產環境中顯示
      if (import.meta.env.DEV) {
        console.warn('Failed to setup logger global access:', error)
      }
    }
  }
}, 0)